import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Check, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Membership() {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [busy, setBusy] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get("/plans").then(r => setPlans(r.data)); }, []);

  const subscribe = async (plan) => {
    if (!user) return nav("/login?next=/membership");
    if ((billing === "monthly" && plan.price_monthly === 0) || (billing === "yearly" && plan.price_yearly === 0)) {
      toast.success("You're already in the free community.");
      return;
    }
    setBusy(plan.tier_key);
    try {
      const { data } = await api.post("/checkout/session", {
        kind: "membership", ref_id: plan.tier_key, billing, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };

  return (
    <div className="bg-cream">
      <div className="brand-container py-20">
        <div className="brand-eyebrow">Membership</div>
        <h1 className="brand-h1 mt-3">Come sit with Judge <span className="script-auntie text-6xl md:text-7xl">Auntie</span>™.</h1>
        <p className="brand-lede mt-6 max-w-2xl">Three tiers, one long table. Whether you're just curious or ready for the Inner Circle, there's a seat here for you.</p>

        <div className="mt-10 inline-flex items-center bg-cream-alt border border-gold-light rounded-full p-1">
          <button onClick={() => setBilling("monthly")} className={`px-5 py-2 text-sm rounded-full transition-colors ${billing === "monthly" ? "bg-plum text-cream" : "text-charcoal/70"}`} data-testid="billing-monthly">Monthly</button>
          <button onClick={() => setBilling("yearly")} className={`px-5 py-2 text-sm rounded-full transition-colors ${billing === "yearly" ? "bg-plum text-cream" : "text-charcoal/70"}`} data-testid="billing-yearly">Yearly · save ~2 months</button>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {plans.map((p) => {
            const price = billing === "yearly" ? p.price_yearly : p.price_monthly;
            return (
              <div key={p.id} className={`editorial-card p-8 relative ${p.highlight ? "border-plum bg-plum text-cream" : ""}`}>
                {p.highlight && <div className="absolute -top-3 left-8 bg-gold text-plum text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold flex items-center gap-1"><Crown className="w-3 h-3" /> Recommended</div>}
                <div className={`brand-eyebrow ${p.highlight ? "!text-gold" : ""}`}>{p.tier_key === "community" ? "Free" : p.tier_key === "chambers" ? "Auntie's Chambers" : "Inner Circle"}</div>
                <h3 className={`mt-3 font-serif text-3xl leading-tight ${p.highlight ? "text-cream" : "text-charcoal"}`}>{p.name}</h3>
                <div className={`mt-6 flex items-baseline gap-2 ${p.highlight ? "text-cream" : "text-charcoal"}`}>
                  <span className="font-serif text-5xl">${price?.toFixed ? price.toFixed(2) : price}</span>
                  <span className={`text-sm ${p.highlight ? "text-cream/70" : "text-charcoal/60"}`}>/ {billing === "yearly" ? "year" : "month"}</span>
                </div>
                <ul className={`mt-8 space-y-3 text-sm ${p.highlight ? "text-cream/85" : "text-charcoal/85"}`}>
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-gold" : "text-plum"}`} /> {f}
                    </li>
                  ))}
                </ul>
                <button disabled={busy === p.tier_key} onClick={() => subscribe(p)}
                  className={`mt-8 w-full ${p.highlight ? "btn-outline !border-gold !text-cream hover:!bg-plum-dark" : "btn-primary"}`}
                  data-testid={`plan-cta-${p.tier_key}`}>
                  {busy === p.tier_key ? "…" : price === 0 ? "Join Free" : "Subscribe"}
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-12 text-xs text-charcoal/50 max-w-3xl">
          Payments are processed securely by Stripe. You can cancel any time from your Account & Billing.
          One-time event tickets do not renew.
        </p>
      </div>
    </div>
  );
}
